// consts/enums

export const DEAL_STRUCTURE_CONTEXT_SCHEMA_VERSION = 1;

export const DEAL_STRUCTURE_CONTEXT_MAX_BYTES = 32 * 1024;

export const DEAL_STRUCTURE_CONTEXT_LIST_CAPS = {
    fees: 25,
    taxes: 10,
    incentives: 15,
    products: 15,
    tradeIns: 4
} as const;

export const DEAL_STRUCTURE_CONTEXT_STRING_CAPS = {
    scopeId: 128,
    dealXgId: 64,
    vin: 17,
    text: 64,
    itemName: 80,
    lenderText: 120
} as const;

export const DEAL_STRUCTURE_CONTEXT_DEAL_TYPES = ['Finance', 'Lease', 'Cash', 'Balloon'] as const;

// interfaces/types

export type DealStructureContextDealType = (typeof DEAL_STRUCTURE_CONTEXT_DEAL_TYPES)[number];

export type DealStructureContextItem = {
    name: string;
    amount: number;
};

export type DealStructureContextRate = {
    apr?: number;
    moneyFactor?: number;
};

export type DealStructureContextTradeIn = {
    year: string;
    make: string;
    model: string;
    trim?: string;
    odometer?: number;
    allowance: number;
    payoff: number;
};

export type DealStructureContext = {
    schemaVersion: typeof DEAL_STRUCTURE_CONTEXT_SCHEMA_VERSION;
    scenario: {
        scopeId: string;
        dealXgId?: string;
        capturedAt: string;
    };
    dealType: DealStructureContextDealType;
    vehicles: {
        sellingVehicle: {
            vin: string;
            stockNumber: string;
            year: string;
            make: string;
            model: string;
            trim: string;
            inventoryType: string;
            odometerMileage: number;
            exteriorColor?: string;
        };
        tradeInVehicles?: {
            tradeInSummary: { totalAllowance: number; totalPayoff: number; netTrade: number };
            itemCount: number;
            tradeIns: DealStructureContextTradeIn[];
        };
    };
    financialDetails: {
        priceDetails: { retailPrice: number; sellingPrice: number; discount?: number };
        paymentDetails?: {
            term: number;
            payment: number;
            amountFinanced?: number;
            totalOfPayments?: number;
            paymentFrequency: string;
            balloon?: { balloonPayment: number; balloonTerm?: number };
        };
        cashDetails?: { totalAmountDue: number; totalAmountDueWithoutProducts?: number };
        rateDetails: { sellRate: DealStructureContextRate; buyRate?: DealStructureContextRate };
        dueInAdvanceDetails: { cashDown: number; netCashDown?: number; dueAtSigning?: number; tradeDown?: number };
        feeDetails: { feeSummary: { totalFees: number }; itemCount: number; fees: DealStructureContextItem[] };
        taxDetails: { taxSummary: { totalTaxes: number }; itemCount: number; taxes: DealStructureContextItem[] };
        incentiveDetails: {
            incentiveSummary: { appliedIncentiveAmount: number };
            itemCount: number;
            incentives: DealStructureContextItem[];
        };
        productDetails: { productSummary: { totalProducts: number }; itemCount: number; products: DealStructureContextItem[] };
        leaseDetails?: { annualMiles: number; residualValue?: number; residualPercent?: number; capCostTotal?: number };
        programDetails?: { lenderName: string; programName?: string };
    };
};
